import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import GroupIcon from '@mui/icons-material/Group';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import EmptyState from '../components/common/EmptyState';
import { useSnackbar } from '../components/common/SnackbarProvider';
import { useParams } from 'react-router-dom';
import axiosInstance from '../api/axiosConfig';
import swimService from '../api/services/swimService';
import { ClassLevel, RosterEntry, RosterResponse } from '../types/swim';
import { useIsMobile } from '../hooks/useIsMobile';

interface SwimProgramLite {
  id: string;
  name: string;
  program_type: string;
  status: string;
  is_active: boolean;
}

const CoachRosterPage: React.FC = () => {
  const isMobile = useIsMobile();
  const snackbar = useSnackbar();
  const { programId } = useParams<{ programId: string }>();
  const [data, setData] = useState<RosterResponse | null>(null);
  const [levels, setLevels] = useState<ClassLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Assessment dialog state
  const [assessFor, setAssessFor] = useState<RosterEntry | null>(null);
  const [assessLevelId, setAssessLevelId] = useState<string>('');
  const [assessNotes, setAssessNotes] = useState('');
  const [assessBusy, setAssessBusy] = useState(false);

  // Transfer dialog state
  const [transferFor, setTransferFor] = useState<RosterEntry | null>(null);
  const [transferTargetId, setTransferTargetId] = useState<string>('');
  const [transferBusy, setTransferBusy] = useState(false);
  const [swimPrograms, setSwimPrograms] = useState<SwimProgramLite[]>([]);

  const [busyEnrollmentId, setBusyEnrollmentId] = useState<string | null>(null);

  useEffect(() => { void load(); }, [programId]);

  async function load() {
    if (!programId) return;
    setLoading(true);
    setError(null);
    try {
      const [roster, lvls, progsRes] = await Promise.all([
        swimService.getRoster(programId),
        swimService.listClassLevels(),
        axiosInstance.get<{ data: SwimProgramLite[] }>('/programs/all'),
      ]);
      setData(roster);
      setLevels(lvls);
      setSwimPrograms(
        (progsRes.data.data ?? []).filter(
          (p) => p.program_type === 'swim' && p.is_active && p.status === 'published' && p.id !== programId,
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load roster');
    } finally {
      setLoading(false);
    }
  }

  async function removeFromProgram(entry: RosterEntry) {
    if (!window.confirm(
      `Remove ${entry.swimmer.first_name} ${entry.swimmer.last_name} from this class?\n\n` +
      "The enrollment will be cancelled. The parent will see it as cancelled in their account.",
    )) return;
    setBusyEnrollmentId(entry.enrollment_id);
    try {
      await swimService.cancelEnrollment(entry.enrollment_id);
      await load();
      snackbar.success(`${entry.swimmer.first_name} removed from this class`);
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Remove failed');
    } finally {
      setBusyEnrollmentId(null);
    }
  }

  function openTransfer(entry: RosterEntry) {
    setTransferFor(entry);
    setTransferTargetId('');
  }

  async function submitTransfer() {
    if (!transferFor || !transferTargetId) return;
    const swimmerName = `${transferFor.swimmer.first_name} ${transferFor.swimmer.last_name}`;
    setTransferBusy(true);
    try {
      await swimService.transferEnrollment(transferFor.enrollment_id, transferTargetId);
      setTransferFor(null);
      await load();
      snackbar.success(`${swimmerName} moved to new class`);
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Transfer failed');
    } finally {
      setTransferBusy(false);
    }
  }

  function openAssessment(entry: RosterEntry) {
    setAssessFor(entry);
    setAssessLevelId('');
    setAssessNotes('');
  }

  async function submitAssessment() {
    if (!assessFor) return;
    const swimmerName = `${assessFor.swimmer.first_name} ${assessFor.swimmer.last_name}`;
    setAssessBusy(true);
    try {
      const swimmer = assessFor.swimmer;
      await swimService.createAssessment({
        dependent_id: swimmer.type === 'dependent' ? String(swimmer.id) : undefined,
        non_member_dependent_id:
          swimmer.type === 'non_member_dependent' ? Number(swimmer.id) : undefined,
        assessed_level_id: assessLevelId || null,
        notes: assessNotes.trim() || undefined,
      });
      setAssessFor(null);
      await load();
      snackbar.success(`Assessment saved for ${swimmerName}`);
    } catch (e) {
      snackbar.error(e instanceof Error ? e.message : 'Assessment failed');
    } finally {
      setAssessBusy(false);
    }
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        spacing={{ xs: 1, sm: 0 }}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" component="h1">Coach Roster</Typography>
          {data?.program?.name && (
            <Typography variant="body2" color="text.secondary">{data.program.name}</Typography>
          )}
        </Box>
        <IconButton onClick={() => void load()} disabled={loading}><RefreshIcon /></IconButton>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : !data || data.count === 0 ? (
        <EmptyState
          icon={<GroupIcon />}
          title="No swimmers enrolled yet"
          body="Parents will appear here once they sign their swimmers up for this class."
        />
      ) : (
        <Stack spacing={1.5}>
          {data.data.map((entry) => {
            const s = entry.swimmer;
            const hasAllergies = !!s.allergies || !!s.medical_conditions;
            return (
              <Card key={entry.enrollment_id} variant="outlined">
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                    spacing={{ xs: 1, sm: 2 }}
                  >
                    <Box>
                      <Typography variant="h6">
                        {s.first_name} {s.last_name}{' '}
                        {s.age != null && <Typography component="span" variant="body2" color="text.secondary">(age {s.age})</Typography>}
                      </Typography>
                      {s.parent_name && (
                        <Typography variant="body2" color="text.secondary">
                          parent: {s.parent_name}
                          {s.parent_phone && (
                            <>
                              {' • '}
                              <Button
                                size="small"
                                startIcon={<PhoneIcon fontSize="small" />}
                                href={`tel:${s.parent_phone}`}
                                sx={{ textTransform: 'none', py: 0 }}
                              >
                                {s.parent_phone}
                              </Button>
                            </>
                          )}
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                      <Button size="small" variant="outlined" onClick={() => openAssessment(entry)}>
                        Add assessment
                      </Button>
                      <IconButton
                        size="small"
                        title="Move to another class"
                        onClick={() => openTransfer(entry)}
                        disabled={busyEnrollmentId === entry.enrollment_id || swimPrograms.length === 0}
                      >
                        <SwapHorizIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        title="Remove from this class"
                        color="error"
                        onClick={() => void removeFromProgram(entry)}
                        disabled={busyEnrollmentId === entry.enrollment_id}
                      >
                        <PersonRemoveIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>

                  {hasAllergies && (
                    <Alert severity="error" icon={<WarningIcon />} sx={{ mt: 1.5 }}>
                      {s.allergies && <div><strong>Allergies:</strong> {s.allergies}</div>}
                      {s.medical_conditions && <div><strong>Medical:</strong> {s.medical_conditions}</div>}
                    </Alert>
                  )}

                  <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    {entry.assessment ? (
                      <Chip
                        size="small"
                        color="info"
                        label={`Last assessed ${entry.assessment.date}${entry.assessment.level ? ` • ${entry.assessment.level}` : ''}`}
                      />
                    ) : (
                      <Chip size="small" color="warning" variant="outlined" label="Not yet assessed" />
                    )}
                    {entry.consent_status && (
                      <Chip
                        size="small"
                        color={entry.consent_status.all_granted ? 'success' : 'warning'}
                        variant="outlined"
                        label={
                          entry.consent_status.all_granted
                            ? 'All consents granted'
                            : `Missing: ${entry.consent_status.missing.join(', ')}`
                        }
                      />
                    )}
                    {s.emergency_contact_phone && (
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<PhoneIcon fontSize="small" />}
                        label={`Emergency: ${s.emergency_contact_name ?? 'Contact'} ${s.emergency_contact_phone}`}
                      />
                    )}
                  </Stack>

                  {entry.assessment?.notes && (
                    <Box mt={1.5}>
                      <Typography variant="caption" color="text.secondary">Latest notes:</Typography>
                      <Typography variant="body2">{entry.assessment.notes}</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Dialog open={assessFor !== null} onClose={() => setAssessFor(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Add assessment{assessFor && ` — ${assessFor.swimmer.first_name} ${assessFor.swimmer.last_name}`}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Assessed level</InputLabel>
              <Select
                value={assessLevelId}
                label="Assessed level"
                onChange={(e) => setAssessLevelId(e.target.value)}
              >
                <MenuItem value=""><em>No level (notes only)</em></MenuItem>
                {levels.map((l) => (
                  <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Notes"
              fullWidth
              multiline
              rows={4}
              value={assessNotes}
              onChange={(e) => setAssessNotes(e.target.value)}
              placeholder="e.g. Comfortable on back float, struggles with breath control on freestyle."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssessFor(null)} disabled={assessBusy}>Cancel</Button>
          <Button onClick={() => void submitAssessment()} variant="contained" disabled={assessBusy}>
            {assessBusy ? 'Saving…' : 'Save assessment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={transferFor !== null} onClose={() => setTransferFor(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Move{transferFor && ` ${transferFor.swimmer.first_name} ${transferFor.swimmer.last_name}`} to another class
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Current enrollment will be cancelled and a new one created in the target class.
              The swimmer's consent grants carry over automatically.
            </Typography>
            <FormControl fullWidth required>
              <InputLabel>Target class</InputLabel>
              <Select
                value={transferTargetId}
                label="Target class"
                onChange={(e) => setTransferTargetId(e.target.value)}
              >
                {swimPrograms.length === 0 && (
                  <MenuItem value="" disabled><em>No other published swim classes available.</em></MenuItem>
                )}
                {swimPrograms.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferFor(null)} disabled={transferBusy}>Cancel</Button>
          <Button
            onClick={() => void submitTransfer()}
            variant="contained"
            disabled={transferBusy || !transferTargetId}
          >
            {transferBusy ? 'Moving…' : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CoachRosterPage;
